import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
  IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  useApiClientDetail,
  useRevokeSecret,
  useRotateSecret,
  useDeleteApiClient,
  useRevokeApiClient,
  useSuspendApiClient,
  useActivateApiClient,
} from '../../../application/hooks/useApiClientDetail';
import { useBreadcrumbLabel } from '../../../../../shared/hooks/useBreadcrumbLabel';
import { useAdminPreferences } from '../../../application/hooks/useAdminPreferences';
import { ApiClientSecretsTable } from './components/ApiClientSecretsTable';
import { ApiClientDangerZone } from './components/ApiClientDangerZone';
import { ConfidentialIcon } from './components/ConfidentialIcon';
import { useNotification } from '../../../../../shared/contexts/useNotification';
import { formatDate } from '../../../../../shared/utils/dateUtils';
import { StatusBadge } from '../../../../../shared/components/ui/StatusBadge';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`api-client-tabpanel-${index}`}
    aria-labelledby={`api-client-tab-${index}`}
  >
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const tabA11yProps = (index: number) => ({
  id: `api-client-tab-${index}`,
  'aria-controls': `api-client-tabpanel-${index}`,
});

const colors = {
  surface: '#f8f9ff',
  onSurface: '#0b1c30',
  onSurfaceVariant: '#434656',
  outline: '#737688',
  outlineVariant: '#c3c5d9',
  surfaceContainerLow: '#eff4ff',
  surfaceContainerLowest: '#ffffff',
  surfaceContainerHigh: '#dce9ff',
  primary: '#003ec7',
  primaryContainer: '#0052ff',
  onPrimary: '#ffffff',
  onPrimaryContainer: '#dfe3ff',
  error: '#ba1a1a',
  errorContainer: '#ffdad6',
};

export const ApiClientDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const { data: client, isLoading, error } = useApiClientDetail(id!);
  const revokeSecret = useRevokeSecret(id!);
  const rotateSecret = useRotateSecret(id!);
  const deleteClient = useDeleteApiClient();
  const revokeClient = useRevokeApiClient();
  const suspendClient = useSuspendApiClient();
  const activateClient = useActivateApiClient();
  const { language, timezone } = useAdminPreferences();
  const { showNotification } = useNotification();

  useBreadcrumbLabel(id, client?.name);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    showNotification('Copied to clipboard', 'success');
  };

  const handleSuspend = async () => {
    if (!id) return;
    try {
      await suspendClient.mutateAsync(id);
      showNotification('API client suspended successfully', 'success');
    } catch {
      showNotification('Failed to suspend API client', 'error');
    }
  };

  const handleActivate = async () => {
    if (!id) return;
    try {
      await activateClient.mutateAsync(id);
      showNotification('API client reactivated successfully', 'success');
    } catch {
      showNotification('Failed to reactivate API client', 'error');
    }
  };

  const handleRevoke = async () => {
    if (!id) return;
    try {
      await revokeClient.mutateAsync(id);
      showNotification('API client revoked successfully', 'success');
    } catch {
      showNotification('Failed to revoke API client', 'error');
    }
  };

  const handleRevokeSecret = async (secretId: string) => {
    try {
      await revokeSecret.mutateAsync(secretId);
      showNotification('Secret revoked successfully', 'success');
    } catch {
      showNotification('Failed to revoke secret', 'error');
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteClient.mutateAsync(id);
      showNotification('API client deleted successfully', 'success');
      navigate('/api-clients');
    } catch {
      showNotification('Failed to delete API client', 'error');
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress sx={{ color: colors.primary }} />
      </Box>
    );
  }

  if (error || !client) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          sx={{
            mb: 2,
            bgcolor: colors.errorContainer,
            color: colors.error,
            borderRadius: '8px',
          }}
        >
          {error instanceof Error ? error.message : 'API Client not found'}
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/api-clients')}
          sx={{
            bgcolor: colors.primary,
            color: colors.onPrimary,
            borderRadius: '8px',
            textTransform: 'none',
            fontFamily: 'Inter, sans-serif',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          Back to List
        </Button>
      </Box>
    );
  }

  const statusMap: Record<string, 'active' | 'suspended' | 'revoked'> = {
    active: 'active',
    suspended: 'suspended',
    revoked: 'revoked',
  };

  return (
    <Box sx={{ p: 4, backgroundColor: colors.surface, minHeight: '100%' }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Typography
          component="h1"
          sx={{
            fontSize: '32px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            color: colors.onSurface,
            lineHeight: '40px',
            letterSpacing: '-0.02em',
          }}
        >
          {client.name}
        </Typography>
        <ConfidentialIcon isConfidential={client.isConfidential as boolean} />
        <StatusBadge
          label={client.status.value}
          status={statusMap[client.status.key] ?? 'active'}
        />
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue: number) => setActiveTab(newValue)}
        aria-label="API Client details tabs"
        sx={{
          borderBottom: `1px solid ${colors.outlineVariant}`,
          mb: 2,
          '& .MuiTabs-indicator': {
            backgroundColor: colors.primary,
            height: '2px',
          },
          '& .MuiTab-root': {
            fontFamily: 'Inter, sans-serif',
            fontSize: '13px',
            fontWeight: 600,
            textTransform: 'none',
            color: colors.outline,
            px: 0,
            mr: 4,
            minWidth: 'auto',
            '&.Mui-selected': {
              color: colors.primary,
            },
          },
        }}
      >
        <Tab label="Overview" {...tabA11yProps(0)} />
        <Tab label={`Secrets (${client.secrets.length})`} {...tabA11yProps(1)} />
      </Tabs>

      {/* Tab Panel 0: Overview */}
      <TabPanel value={activeTab} index={0}>
        {/* Client Information */}
        <Box
          sx={{
            backgroundColor: colors.surfaceContainerLowest,
            border: `1px solid ${colors.outlineVariant}`,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0px 1px 3px rgba(0,0,0,0.05), 0px 10px 15px -5px rgba(0,0,0,0.03)',
          }}
        >
          <Box
            sx={{
              px: 4,
              py: 3,
              borderBottom: `1px solid ${colors.outlineVariant}`,
              backgroundColor: 'rgba(239, 244, 255, 0.5)',
            }}
          >
            <Typography
              sx={{
                fontSize: '20px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                color: colors.onSurface,
                lineHeight: '28px',
              }}
            >
              Client Information
            </Typography>
          </Box>

          <Box
            sx={{
              p: 4,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' },
              gapX: 6,
              gapY: 4,
            }}
          >
            {/* Client ID */}
            <Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  color: colors.outline,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: 1,
                }}
              >
                Client ID
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontFamily: 'Courier Prime, monospace',
                    color: colors.primaryContainer,
                    backgroundColor: colors.surfaceContainerLow,
                    px: 1,
                    py: 0.5,
                    borderRadius: '4px',
                  }}
                >
                  {client.clientId}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleCopy(client.clientId)}
                  sx={{ color: colors.outline, '&:hover': { color: colors.primary } }}
                >
                  <ContentCopyIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>

            {/* Client Type */}
            <Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  color: colors.outline,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: 1,
                }}
              >
                Client Type
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  color: colors.onSurface,
                  textTransform: 'capitalize',
                }}
              >
                {client.clientType}
              </Typography>
            </Box>

            {/* Tenant ID */}
            <Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  color: colors.outline,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: 1,
                }}
              >
                Tenant ID
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontFamily: 'Courier Prime, monospace',
                  color: colors.onSurfaceVariant,
                }}
              >
                {client.tenantId || '00000000-0000-0000-0000-000000000000'}
              </Typography>
            </Box>

            {/* Scopes */}
            <Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  color: colors.outline,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: 1,
                }}
              >
                Scopes
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {client.scopes.length === 0 ? (
                  <Typography sx={{ fontSize: '14px', color: colors.onSurfaceVariant }}>
                    -
                  </Typography>
                ) : (
                  client.scopes.map((scope) => (
                    <Chip
                      key={scope}
                      label={scope}
                      size="small"
                      sx={{
                        fontSize: '12px',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        backgroundColor: 'rgba(0, 62, 199, 0.1)',
                        color: colors.primary,
                        border: `1px solid rgba(0, 62, 199, 0.2)`,
                        borderRadius: '4px',
                      }}
                    />
                  ))
                )}
              </Box>
            </Box>

            {/* Grant Types */}
            <Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  color: colors.outline,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: 1,
                }}
              >
                Grant Types
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {client.grantTypes.length === 0 ? (
                  <Typography sx={{ fontSize: '14px', color: colors.onSurfaceVariant }}>
                    -
                  </Typography>
                ) : (
                  client.grantTypes.map((gt) => (
                    <Chip
                      key={gt}
                      label={gt}
                      size="small"
                      sx={{
                        fontSize: '12px',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        backgroundColor: colors.surfaceContainerLow,
                        color: colors.onSurfaceVariant,
                        border: `1px solid rgba(115, 118, 136, 0.2)`,
                        borderRadius: '4px',
                      }}
                    />
                  ))
                )}
              </Box>
            </Box>

            {/* Token TTL */}
            <Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  color: colors.outline,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: 1,
                }}
              >
                Token TTL
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  color: colors.onSurface,
                }}
              >
                {client.tokenTtlSeconds} seconds
              </Typography>
            </Box>

            {/* Requires PKCE */}
            <Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  color: colors.outline,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: 1,
                }}
              >
                Requires PKCE
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  color: colors.onSurface,
                }}
              >
                {client.requiresPkce ? 'Yes' : 'No'}
              </Typography>
            </Box>

            {/* Created At */}
            <Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  color: colors.outline,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: 1,
                }}
              >
                Created At
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon sx={{ fontSize: 18, color: colors.outline }} />
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif',
                    color: colors.onSurfaceVariant,
                  }}
                >
                  {formatDate(client.createdAt, { language, timezone, includeTime: true })}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Usage Activity & Stats */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            gap: 3,
            mt: 4,
          }}
        >
          {/* Usage Chart */}
          <Box
            sx={{
              backgroundColor: colors.surfaceContainerLow,
              border: `1px solid ${colors.outlineVariant}`,
              borderRadius: '12px',
              p: 4,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{
                fontSize: '13px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                color: colors.primary,
                mb: 1,
              }}
            >
              Usage Activity
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                color: colors.onSurfaceVariant,
                mb: 4,
              }}
            >
              Last 30 days of API calls for this client.
            </Typography>
            <Box sx={{ height: 128, display: 'flex', alignItems: 'end', gap: 1 }}>
              {[40, 60, 45, 80, 70, 90, 55, 65, 75, 40, 85, 60].map((height, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: 1,
                    height: `${height}%`,
                    backgroundColor: 'rgba(0, 62, 199, 0.2)',
                    borderRadius: '2px 2px 0 0',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: colors.primary,
                    },
                  }}
                  title={`Day ${index + 1}`}
                />
              ))}
            </Box>
          </Box>

          {/* Total Requests */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #0052ff 0%, #003ec7 100%)',
              borderRadius: '12px',
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              color: colors.onPrimaryContainer,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  color: colors.onPrimaryContainer,
                  opacity: 0.8,
                  mb: 1,
                }}
              >
                Total Requests
              </Typography>
              <Typography
                sx={{
                  fontSize: '32px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  color: colors.onPrimary,
                  lineHeight: '40px',
                  letterSpacing: '-0.02em',
                }}
              >
                1.2M
              </Typography>
            </Box>
            <Box
              sx={{
                pt: 3,
                mt: 3,
                borderTop: `1px solid rgba(223, 227, 255, 0.2)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Inter, sans-serif',
                  color: colors.onPrimaryContainer,
                }}
              >
                +12.5% from last week
              </Typography>
              <TrendingUpIcon sx={{ color: colors.onPrimaryContainer }} />
            </Box>
          </Box>
        </Box>
      </TabPanel>

      {/* Tab Panel 1: Secrets */}
      <TabPanel value={activeTab} index={1}>
        <Box
          sx={{
            mt: 2,
            backgroundColor: colors.surfaceContainerLowest,
            pt: 3,
            pb: 0,
            borderRadius: '12px',
            border: `1px solid ${colors.outlineVariant}`,
            overflow: 'hidden',
            boxShadow: '0px 1px 3px rgba(0,0,0,0.05), 0px 10px 15px -5px rgba(0,0,0,0.03)',
          }}
        >
          <Typography
            sx={{
              px: 3,
              fontSize: '20px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              color: colors.onSurface,
            }}
          >
            Client Secrets
          </Typography>
          <Typography
            sx={{
              px: 3,
              mb: 2,
              fontSize: '14px',
              fontFamily: 'Inter, sans-serif',
              color: colors.onSurfaceVariant,
            }}
          >
            Manage secrets for this API client. You can rotate secrets to generate new credentials
            or revoke them to immediately invalidate access.
          </Typography>

          <ApiClientSecretsTable
            secrets={client.secrets}
            apiClientId={client.id}
            onRevoke={handleRevokeSecret}
            onRotate={rotateSecret.mutateAsync}
          />
        </Box>
      </TabPanel>

      {/* Danger Zone - Suspend Client */}
      {client.status.key !== 'suspended' && client.status.key !== 'revoked' && (
        <ApiClientDangerZone
          title="Suspend API Client"
          description="Suspending will temporarily disable this API client. New token requests will be rejected, but the client can be reactivated later."
          buttonLabel="Suspend Client"
          buttonColor="warning"
          isActive={client.status.key === 'active'}
          onAction={handleSuspend}
          isLoading={suspendClient.isPending}
        />
      )}

      {/* Danger Zone - Reactivate Client */}
      {client.status.key === 'suspended' && (
        <ApiClientDangerZone
          title="Reactivate API Client"
          description="Reactivating will restore this API client and allow new token requests."
          buttonLabel="Reactivate Client"
          buttonColor="warning"
          isActive={true}
          onAction={handleActivate}
          isLoading={activateClient.isPending}
        />
      )}

      {/* Danger Zone - Revoke Client */}
      {client.status.key !== 'revoked' && (
        <ApiClientDangerZone
          title="Revoke API Client"
          description="Revoking will permanently disable this API client. This action cannot be undone."
          buttonLabel="Revoke Client"
          buttonColor="warning"
          isActive={client.status.key !== 'revoked'}
          onAction={handleRevoke}
          isLoading={revokeClient.isPending}
        />
      )}

      {/* Danger Zone - Delete Client */}
      <ApiClientDangerZone
        title="Delete API Client"
        description="This action cannot be undone. All secrets and configuration will be permanently removed."
        buttonLabel={`Delete "${client.name}"`}
        buttonColor="error"
        isActive={true}
        onAction={handleDelete}
        isLoading={deleteClient.isPending}
      />
    </Box>
  );
};
